/** Repeatable slatted concrete deck tile. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const TILE = 1.2;
const GAP = 0.018;
const SLAT_WIDTH = (TILE - 10 * GAP) / 10;

function buildDeckGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  let offset = 0;
  for (let i = 0; i < 10; i++) {
    const source = new THREE.BoxGeometry(SLAT_WIDTH, 0.09, TILE);
    source.translate(-TILE / 2 + GAP / 2 + SLAT_WIDTH / 2 + i * (SLAT_WIDTH + GAP), 0.045, 0);
    const position = source.getAttribute('position');
    const normal = source.getAttribute('normal');
    const index = source.getIndex();
    for (let vertex = 0; vertex < position.count; vertex++) {
      positions.push(position.getX(vertex), position.getY(vertex), position.getZ(vertex));
      normals.push(normal.getX(vertex), normal.getY(vertex), normal.getZ(vertex));
    }
    if (index) for (let vertex = 0; vertex < index.count; vertex++) indices.push(index.getX(vertex) + offset);
    offset += position.count;
    source.dispose();
  }
  const deck = new THREE.BufferGeometry();
  deck.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  deck.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  deck.setIndex(indices);
  return deck;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'slat-floor';
  const concrete = surface('mineral', 0x818984);
  // Start and finish half a gap inside the tile. Adjacent tiles therefore make
  // one normal 18 mm opening at their boundary, instead of a conspicuous double
  // joint which was the failure mode of full-width edge slats.
  const deck = new THREE.Mesh(buildDeckGeometry(), concrete);
  deck.name = 'slat-deck';
  deck.receiveShadow = true;
  group.add(deck);
  return group;
}

export const slatFloorAsset: AssetDefinition = {
  id: 'slat-floor', label: 'Slat floor', description: '1.2 m concrete deck tile with 18 mm drainage gaps and seamless edge cadence.',
  footprint: { width: 1.2, height: 0.09, length: 1.2 }, create: build,
};
